export {ChangePassword as Page};

import {Helmet} from "react-helmet-async";
import {useEffect, useState, useContext} from "preact/hooks";
import {EmailAuthProvider, reauthenticateWithCredential, updatePassword} from "firebase/auth";
import {AccountSidebar} from "../../../components/AccountSidebar.jsx";
import messages from "../../../messages/messages.js";
import {NotificationContext} from '../../../components/NotificationContext.jsx';
import PasswordRequirements, { validateAccount } from '../../../components/PasswordRequirements.jsx';
import {useAuth} from "../../../components/AuthProvider.jsx";
import {CircleSpinner, RingSpinner} from "../../../components/Spinner.jsx";

function ChangePassword() {

    const {addNotification} = useContext(NotificationContext);

    const {user, authLoading} = useAuth();
    const [pageLoading, setPageLoading] = useState(true);
    const [currentPassword, setCurrentPassword] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (authLoading) return;

        if (!user) return;

        setPageLoading(false);
    }, [authLoading, user])

    const changePassword = async (e) => {
        e.preventDefault();
        setLoading(true);

        const check = validateAccount({ currentPassword, password, confirmPassword, requireCurrent: true });
        if (!check.ok) {
            addNotification(check.message, 'error');
            setLoading(false);
            return;
        }

        // Reauthenticate user
        const credential = await EmailAuthProvider.credential(user.email, currentPassword);
        try {
            await reauthenticateWithCredential(user, credential);
        } catch (error) {
            addNotification(error.message, 'error');
            setLoading(false);
            return;
        }
        // Update password
        updatePassword(user, password).then(() => {
            setPassword("");
            setConfirmPassword("");
            addNotification('Password updated successfully. This will take effect the next time you log in.', 'success');
        }).catch((error) => {
            addNotification(error.message, 'error');
        }).finally(() => {
            setLoading(false);
        });
    }

    return (
        <section className="text-white">

            <Helmet>
                <title>{messages["SITE.NAME"]} | My channel artwork</title>
                <meta name="description" content="My channel artwork"/>
            </Helmet>

            <div>

                <div className="flex flex-wrap">

                    <AccountSidebar/>

                    {pageLoading ? (
                        <div className="w-full md:w-3/4 px-0 md:px-4">
                            <div className="p-6 text-white rounded-lg bg-zinc-800">
                                <div className="flex items-center">
                                    <CircleSpinner/>
                                    <h3 className="text-lg m-0">Loading...</h3>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="w-full md:w-3/4 px-0 md:px-4">
                            <form onSubmit={changePassword} className="border border-border/60 bg-zinc-800 rounded-lg p-6 mb-3">
                                <h2 className="text-2xl font-semibold mb-4">Change password</h2>

                                <div className="flex flex-wrap -mx-3 mb-6">
                                    <div className="w-full md:w-1/2 px-3 mb-3">
                                        <label htmlFor="current-password"
                                               className="block mb-2 text-sm font-medium text-white">Current
                                            Password</label>
                                        <input type="password" name="current-password" id="current-password"
                                               placeholder="••••••••"
                                               value={currentPassword}
                                               onChange={(e) => setCurrentPassword(e.target.value)}
                                               className="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg block w-full p-2.5"
                                               required=""/>
                                    </div>
                                    <div className="w-full md:w-1/2 px-3">
                                    </div>
                                    <div className="w-full md:w-1/2 px-3">
                                        <label htmlFor="password"
                                               className="block mb-2 text-sm font-medium text-white">New
                                            Password</label>
                                        <input type="password" name="password" id="password" placeholder="••••••••"
                                               value={password}
                                               onChange={(e) => setPassword(e.target.value)}
                                               className="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg block w-full p-2.5"
                                               required=""/>
                                    </div>
                                    <div className="w-full md:w-1/2 px-3">
                                        <label htmlFor="confirm-password"
                                               className="block mb-2 text-sm font-medium text-white">Confirm
                                            New Password</label>
                                        <input type="password" name="confirm-password" id="confirm-password"
                                               placeholder="••••••••"
                                               value={confirmPassword}
                                               onChange={(e) => setConfirmPassword(e.target.value)}
                                               className="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg block w-full p-2.5"
                                               required=""/>
                                    </div>
                                </div>

                                <PasswordRequirements password={password} />

                                <button type="submit"
                                        className="w-full cursor-pointer text-white border border-white hover:bg-white hover:text-black font-medium rounded-lg text-sm mt-2 px-5 py-2.5 text-center">Reset
                                    password {loading && <RingSpinner/>}
                                </button>
                            </form>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}
